import { memo, useMemo } from 'react';
import type { CompileResult } from '../engine/types';
import { useI18n } from '../i18n';
import { ROW_LABEL_RE, isHeadingLine, readFrontMatter } from '../lib/patternSyntax';
import { rewriteLineForSize, type ReaderSegment } from '../lib/sizeRewrite';
import { sizeName } from '../lib/sizes';
import { Icon } from '../ui/Icon';
import { SizeChips } from './SizeChips';

/**
 * « Ma taille »: the pattern as a knitter reads it, with only the chosen size's numbers. Read-only
 * and printable; the engine keeps checking the full multi-size text.
 */
export const ReaderView = memo(function ReaderView({
  text,
  result,
  size,
  onSize,
  onBack,
  onPrint,
}: {
  text: string;
  result: CompileResult | null;
  size: number;
  onSize: (s: number) => void;
  onBack: () => void;
  onPrint: () => void;
}) {
  const { t, lang } = useI18n();
  const sizes = result?.document.sizes ?? [];
  const count = sizes.length;
  const name = sizeName(result, size, lang);
  const patternLang = result?.document.lang;
  const { lines, title } = useMemo(() => {
    const src = text.replace(/\r\n?/g, '\n').split('\n');
    const { meta, lineCount } = readFrontMatter(text);
    return { lines: src.map((l, i) => ({ no: i + 1, text: l })).slice(lineCount), title: result?.document.title ?? meta.title ?? null };
  }, [text, result?.document.title]);
  const notForSize = useMemo(() => {
    const set = new Set<number>();
    for (const r of result?.rows ?? []) if (r.per_size.some((p) => p.size === size && !p.applies)) set.add(r.line_no);
    return set;
  }, [result?.rows, size]);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-editor" data-testid="reader" data-print-area="reader">
      <div className="mx-auto max-w-measure px-6 pb-24 pt-8 sm:px-10">
        <div className="flex flex-wrap items-center gap-3" data-print-hide>
          <button type="button" className="btn-quiet -ml-2" onClick={onBack} data-testid="reader-back">
            <Icon name="arrow" size={12} className="rotate-180" />
            {t('reader.back')}
          </button>
          <button type="button" className="btn-secondary ml-auto" onClick={onPrint} data-testid="reader-print">
            <Icon name="print" size={14} />
            {t('reader.print')}
          </button>
        </div>
        <p className="kicker mt-6">
          {t('reader.title')} · {name}
        </p>
        {title && (
          <h2 className="font-display mt-2 text-3xl font-[420] leading-tight text-fg" data-verbatim lang={patternLang}>
            {title}
          </h2>
        )}
        {count > 1 ? (
          <div className="mt-4 flex flex-wrap items-center gap-4" data-print-hide>
            <SizeChips sizes={sizes} selected={size} onSelect={(s) => onSize(s ?? 0)} withAll={false} label={t('reader.pick')} testId="reader-size-chips" />
          </div>
        ) : null}
        <p className="mt-3 text-xs leading-relaxed text-muted">{count > 1 ? t('reader.caption', { size: name }) : t('reader.oneSize')}</p>

        <div className="mt-8 space-y-1 text-[15px] leading-[1.75] text-fg" aria-label={t('reader.aria', { size: name })} role="document">
          {lines.every((l) => l.text.trim() === '') && <p className="text-muted">{t('reader.empty')}</p>}
          {lines.map(({ no, text: line }) => {
            if (line.trim() === '') return <div key={no} className="h-3" aria-hidden="true" />;
            if (isHeadingLine(line))
              return (
                <h3 key={no} className="font-display pb-1 pt-5 text-xl font-[460]" data-verbatim lang={patternLang}>
                  {line.trim()}
                </h3>
              );
            const m = ROW_LABEL_RE.exec(line);
            const label = m ? m[0] : '';
            const rest = m ? line.slice(m[0].length) : line;
            const dim = notForSize.has(no);
            return (
              <p key={no} className={dim ? 'text-subtle' : ''} data-line={no}>
                <span className={dim ? 'line-through decoration-1' : ''} data-verbatim lang={patternLang}>
                  {label && <span className="font-display font-[560] text-label">{label}</span>}
                  <Segments segments={rewriteLineForSize(rest, size, count)} />
                </span>
                {dim && <span className="ml-2 text-2xs">({t('reader.notForSize', { size: name })})</span>}
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
});

function Segments({ segments }: { segments: ReaderSegment[] }) {
  const { t } = useI18n();
  return (
    <>
      {segments.map((s, i) =>
        s.picked ? (
          <mark
            key={i}
            className="rounded-sm bg-heather/[0.14] px-0.5 font-semibold text-fg"
            title={t('reader.fromGroup', { group: s.from ?? '' })}
            data-testid="reader-value"
          >
            {s.text}
          </mark>
        ) : (
          <span key={i}>{s.text}</span>
        ),
      )}
    </>
  );
}
